"use client"

import React, { createContext, useContext, useState, useEffect } from "react"

export type Language = "en" | "de" | "fr" | "es" | "hi"

interface LanguageContextType {
    language: Language
    setLanguage: (lang: Language) => void
    t: (key: string) => string
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined)

const TRANSLATIONS: Record<Language, Record<string, string>> = {
    en: {
        "nav.manufacturers": "Manufacturers",
        "nav.products": "Products",
        "nav.request_part": "Request a spare part",
        "nav.industrial_purchase": "Industrial purchase",
        "nav.company_details": "Company Details",
        "nav.contact": "Contact",
        "nav.faq": "FAQ",
        "nav.reviews": "Reviews",

        "nav.payment": "Payment",
        "nav.returns": "Returns",
        "nav.shipment": "Shipment",
        "nav.conditions": "Conditions",
        "header.search": "Search",
        "header.contact_support": "Contact & Support",
        "header.subtotal": "Subtotal",
        "header.search_products": "Search products",
        "header.user_account": "User account",
        "header.close_menu": "Close menu",
        "header.open_menu": "Open menu",
        "featured.title": "Featured Products",
        "featured.subtitle": "Explore our most popular industrial components and spare parts",
        "products.view_details": "View Details",
        "products.add_to_cart": "Add to Cart",
        "products.added": "Added",
        "products.in_stock": "In Stock",
        "products.out_of_stock": "Out of Stock",
        "products.view_all": "View All Products",
        "filters.search_placeholder": "Search products by name, type, or description...",
        "filters.active_filters": "Active filters:",
        "filters.clear_all": "Clear all",
        "filters.showing_results": "Showing results",
        "filters.no_results": "No products found",
        "banner.shipping": "Free standard shipping in India from ₹10,000 purchase value →",
        "wishlist.title": "My Wishlist",
        "wishlist.items": "items",
        "wishlist.empty_title": "Your Wishlist is Empty",
        "wishlist.empty_subtitle": "Start adding items to your wishlist to save them for later",
        "wishlist.browse": "Browse Products",
        "contact.title": "Get in Touch",
        "contact.subtitle": "Have questions about our products or services? We're here to help.",
        "contact.send_message": "Send us a Message",
        "contact.success_title": "Message Sent!",
        "contact.success_msg": "Thank you for contacting us. We'll get back to you soon.",
        "contact.chat_whatsapp": "Chat on WhatsApp",
        "contact.send_another": "Send Another Message",
        "hero.title": "Industrial Spare Parts & Components",
        "hero.subtitle": "Your trusted partner for high-quality industrial spare parts, bearings, pumps, motors, and components. With over 20 years of industry expertise, we deliver reliability and excellence.",
        "hero.browse": "Browse Products",
        "account.my_orders": "My Orders",
        "account.view_all": "View All",
        "account.no_orders": "No orders yet",
        "account.no_orders_msg": "When you place an order, it will appear here.",
        "account.start_shopping": "Start Shopping",
        "account.wishlist": "Wishlist",
        "account.wishlist_empty": "Your wishlist is empty",
        "account.my_cart": "My Cart",
        "account.cart_empty": "Your cart is empty",
        "account.phone": "Phone",
        "account.address": "Address",
        "account.member_status": "Member Status",
        "account.active": "Active",
        "account.joined": "Joined",
        "account.edit_profile": "Edit Profile",
        "account.update_info": "Update your information",
        "account.title": "My Account",
        "account.hello": "Hello",
        "account.dashboard": "Dashboard",
        "account.sign_out": "Sign Out",
        "account.recent_activity": "Track your recent purchases",
        "account.manage_desc": "Manage your profile, orders and preferences",
        "hero.contact": "Get in Touch",
        "products.dynamic_price": "Prices are dynamic, this is an estimate costing, contact us after placing order for final quote and invoice.",
        "products.only_available": "Only {count} available - order now!",
        "products.condition": "Condition",
        "products.condition_used": "Used",
        "products.trust_payment": "Payment on invoice - no account needed",
        "products.trust_shipping": "Free shipping",
        "products.trust_delivery": "Next day delivery available",
        "products.info": "Product Information",
        "products.quantity": "Quantity",
        "products.sku": "SKU",
        "products.bulk_title": "Bulk Order Inquiry",
        "products.bulk_send": "Send Inquiry",
        "products.desc": "Description",
        "products.specs": "Specifications",
        "products.shipping": "Shipping",
        "footer.company": "Company",
        "footer.about": "About Us",
        "footer.all_products": "All Products",
        "footer.support": "Support",
        "footer.legal": "Legal",
        "footer.privacy": "Privacy Policy",
        "footer.terms": "Terms of Service",
        "footer.rights": "All rights reserved.",
        "category.pumps": "Pumps",
        "category.motors": "Motors",
        "faq.ordering": "Ordering",
        "faq.shipping": "Shipping",
        "faq.returns": "Returns",
        "faq.payment_methods_q": "What payment methods do you accept?",
        "faq.payment_methods_a": "We accept bank transfer (Proforma), PayPal, iDeal, Mastercard, and Visa. Commercial customers and public institutions can cũng pay on account.",
        "faq.bulk_order_q": "Can I place a bulk order?",
        "faq.bulk_order_a": "Yes! We offer special pricing for bulk orders. Please contact our sales team at sales@unispare.in or call +91 97188 89253.",
        "faq.shipping_time_q": "How long does shipping take?",
        "faq.shipping_time_a": "Standard delivery takes 3-6 working days for EU and US, and 6-10 working days elsewhere. Orders before 4 PM ship the same day via UPS/Schenker.",
        "faq.international_q": "Do you ship internationally?",
        "faq.international_a": "Yes, we ship worldwide. Outside the EU, orders are sent DDU (Delivered Duty Unpaid) - customers are responsible for local duties and taxes.",
        "faq.return_policy_q": "What is your return policy?",
        "faq.return_policy_a": "We offer a 14-day right of return on all products (30 days for US/Canada). Items must be in original condition with tags.",
        "faq.return_process_q": "How do I return an item?",
        "faq.return_process_a": "Returns must be submitted via our returns portal. Please note that return shipping costs and any customs duties are the customer's responsibility.",
        "request_part.title": "Request a Spare Part",
        "request_part.subtitle": "Don't find the spare part you need? Submit a request and our team will help you locate it.",
        "request_part.success_title": "Request Submitted Successfully!",
        "request_part.success_desc": "Thank you for your request. Our team will get back to you shortly.",
        "request_part.prod_name": "Product Name *",
        "request_part.prod_name_placeholder": "Enter product name or model number",
        "request_part.manufacturer": "Manufacturer *",
        "request_part.manufacturer_placeholder": "Enter manufacturer name",
        "request_part.model": "Model/Serial Number",
        "request_part.model_placeholder": "Enter model or serial number",
        "request_part.desc": "Description *",
        "request_part.desc_placeholder": "Describe the spare part you're looking for",
        "request_part.email": "Your Email *",
        "request_part.email_placeholder": "your@email.com",
        "request_part.phone": "Phone Number",
        "request_part.phone_placeholder": "+49 40 XXXX XXXX",
        "request_part.submit": "Submit Request",
        "request_part.submitting": "Submitting Request..."
    },
    de: {
        "nav.manufacturers": "Hersteller",
        "nav.products": "Produkte",
        "nav.request_part": "Ersatzteil anfragen",
        "nav.industrial_purchase": "Industrieller Einkauf",
        "nav.company_details": "Unternehmensdetails",
        "nav.contact": "Kontakt",
        "nav.faq": "FAQ",
        "nav.reviews": "Bewertungen",

        "nav.payment": "Zahlung",
        "nav.returns": "Retouren",
        "nav.shipment": "Versand",
        "nav.conditions": "Bedingungen",
        "header.search": "Suche",
        "header.contact_support": "Kontakt & Support",
        "header.subtotal": "Zwischensumme",
        "header.search_products": "Produkte suchen",
        "header.user_account": "Benutzerkonto",
        "header.close_menu": "Menü schließen",
        "header.open_menu": "Menü öffnen",
        "featured.title": "Highlight-Produkte",
        "featured.subtitle": "Entdecken Sie unsere beliebtesten Industriekomponenten",
        "products.view_details": "Details anzeigen",
        "products.add_to_cart": "In den Warenkorb",
        "products.added": "Hinzugefügt",
        "products.in_stock": "Auf Lager",
        "products.out_of_stock": "Ausverkauft",
        "products.view_all": "Alle Produkte ansehen",
        "filters.search_placeholder": "Suche nach Name, Typ oder Beschreibung...",
        "filters.active_filters": "Aktive Filter:",
        "filters.clear_all": "Alles löschen",
        "filters.showing_results": "Zeige Ergebnisse",
        "filters.no_results": "Keine Produkte gefunden",
        "banner.shipping": "Kostenloser Standardversand in Deutschland ab 100€ Bestellwert →",
        "wishlist.title": "Mein Wunschzettel",
        "wishlist.items": "Artikel",
        "wishlist.empty_title": "Ihr Wunschzettel ist leer",
        "wishlist.empty_subtitle": "Fügen Sie Artikel zu Ihrem Wunschzettel hinzu, um sie für später zu speichern",
        "wishlist.browse": "Produkte durchsuchen",
        "contact.title": "Kontaktieren Sie uns",
        "contact.subtitle": "Haben Sie Fragen zu unseren Produkten? Wir sind hier, um zu helfen.",
        "contact.send_message": "Schreiben Sie uns",
        "contact.success_title": "Nachricht gesendet!",
        "contact.success_msg": "Vielen Dank für Ihre Kontaktaufnahme. Wir melden uns bald bei Ihnen.",
        "contact.chat_whatsapp": "Auf WhatsApp chatten",
        "contact.send_another": "Weitere Nachricht senden",
        "hero.title": "Industrielle Ersatzteile & Komponenten",
        "hero.subtitle": "Ihr zuverlässiger Partner für hochwertige industrielle Ersatzteile, Lager, Pumpen, Motoren und Komponenten. Mit über 20 Jahren Branchenerfahrung liefern wir Zuverlässigkeit und Exzellenz.",
        "hero.browse": "Produkte durchsuchen",
        "account.my_orders": "Meine Bestellungen",
        "account.view_all": "Alle ansehen",
        "account.no_orders": "Noch keine Bestellungen",
        "account.no_orders_msg": "Sobald Sie eine Bestellung aufgeben, wird sie hier angezeigt.",
        "account.start_shopping": "Einkauf starten",
        "account.wishlist": "Wunschliste",
        "account.wishlist_empty": "Ihre Wunschliste ist leer",
        "account.my_cart": "Mein Warenkorb",
        "account.cart_empty": "Ihr Warenkorb ist leer",
        "account.phone": "Telefon",
        "account.address": "Adresse",
        "account.member_status": "Mitgliedsstatus",
        "account.active": "Aktiv",
        "account.joined": "Beigetreten",
        "account.edit_profile": "Profil bearbeiten",
        "account.update_info": "Ihre Informationen aktualisieren",
        "account.title": "Mein Konto",
        "account.hello": "Hallo",
        "account.dashboard": "Dashboard",
        "account.sign_out": "Abmelden",
        "account.recent_activity": "Ihre letzten Einkäufe verfolgen",
        "account.manage_desc": "Verwalten Sie Ihr Profil, Ihre Bestellungen und Präferenzen",
        "hero.contact": "Kontakt aufnehmen",
        "products.dynamic_price": "Die Preise sind dynamisch, dies ist eine Schätzung. Kontaktieren Sie uns nach der Bestellung für ein endgültiges Angebot und eine Rechnung.",
        "products.only_available": "Nur noch {count} verfügbar - jetzt bestellen!",
        "products.condition": "Zustand",
        "products.condition_used": "Gebraucht",
        "products.trust_payment": "Zahlung auf Rechnung - kein Konto erforderlich",
        "products.trust_shipping": "Kostenloser Versand",
        "products.trust_delivery": "Lieferung am nächsten Tag möglich",
        "products.info": "Produktinformationen",
        "products.quantity": "Menge",
        "products.sku": "SKU",
        "products.bulk_title": "Anfrage für Großbestellung",
        "products.bulk_send": "Anfrage senden",
        "products.desc": "Beschreibung",
        "products.specs": "Spezifikationen",
        "products.shipping": "Versand",
        "footer.company": "Unternehmen",
        "footer.about": "Über uns",
        "footer.all_products": "Alle Produkte",
        "footer.support": "Support",
        "footer.legal": "Rechtliches",
        "footer.privacy": "Datenschutzerklärung",
        "footer.terms": "Nutzungsbedingungen",
        "footer.rights": "Alle Rechte vorbehalten.",
        "category.pumps": "Pumpen",
        "category.motors": "Motoren",
        "faq.ordering": "Bestellung",
        "faq.shipping": "Versand",
        "faq.returns": "Retouren",
        "faq.payment_methods_q": "Welche Zahlungsmethoden akzeptieren Sie?",
        "faq.payment_methods_a": "Wir akzeptieren Banküberweisung (Proforma), PayPal, iDeal, Mastercard und Visa. Geschäftskunden und öffentliche Einrichtungen können auch auf Rechnung bezahlen.",
        "faq.bulk_order_q": "Kann ich eine Großbestellung aufgeben?",
        "faq.bulk_order_a": "Ja! Wir bieten Sonderpreise für Großbestellungen an. Bitte kontaktieren Sie unser Verkaufsteam unter sales@unispare.in oder rufen Sie +91 97188 89253 an.",
        "faq.shipping_time_q": "Wie lange dauert der Versand?",
        "faq.shipping_time_a": "Die Standardlieferung dauert 3-6 Werktage für EU und USA, und 6-10 Werktage weltweit. Bestellungen vor 16 Uhr werden am selben Tag per UPS/Schenker versandt.",
        "faq.international_q": "Versenden Sie international?",
        "faq.international_a": "Ja, wir versenden weltweit. Außerhalb der EU erfolgt der Versand DDU (unverzollt) - Kunden sind für lokale Zölle und Steuern verantwortlich.",
        "faq.return_policy_q": "Wie ist Ihre Rückगाबepolitik?",
        "faq.return_policy_a": "Wir bieten ein 14-tägiges Rückgaberecht für alle Produkte (30 Tage für USA/Kanada). Artikel müssen im Originalzustand mit Etiketten sein.",
        "faq.return_process_q": "Wie sende ich einen Artikel zurück?",
        "faq.return_process_a": "Retouren müssen über unser Retourenportal eingereicht werden. Bitte beachten Sie, dass Rücksendekosten und etwaige Zollgebühren vom Kunden zu tragen sind.",
        "request_part.title": "Ersatzteil anfragen",
        "request_part.subtitle": "Sie finden das benötigte Ersatzteil nicht? Senden Sie eine Anfrage und unser Team hilft Ihnen bei der Suche.",
        "request_part.success_title": "Anfrage erfolgreich übermittelt!",
        "request_part.success_desc": "Vielen Dank für Ihre Anfrage. Unser Team wird sich in Kürze mit Ihnen in Verbindung setzen.",
        "request_part.prod_name": "Produktname *",
        "request_part.prod_name_placeholder": "Geben Sie den Produktnamen oder die Modellnummer ein",
        "request_part.manufacturer": "Hersteller *",
        "request_part.manufacturer_placeholder": "Geben Sie den Herstellernamen ein",
        "request_part.model": "Modell-/Seriennummer",
        "request_part.model_placeholder": "Geben Sie die Modell- oder Seriennummer ein",
        "request_part.desc": "Beschreibung *",
        "request_part.desc_placeholder": "Beschreiben Sie das gesuchte Ersatzteil",
        "request_part.email": "Ihre E-Mail *",
        "request_part.email_placeholder": "ihre@email.de",
        "request_part.phone": "Telefonnummer",
        "request_part.phone_placeholder": "+49 40 XXXX XXXX",
        "request_part.submit": "Anfrage absenden",
        "request_part.submitting": "Anfrage wird gesendet..."
    },
    fr: {
        "nav.manufacturers": "Fabricants",
        "nav.products": "Produits",
        "nav.request_part": "Demander une pièce",
        "nav.industrial_purchase": "Achat industriel",
        "nav.company_details": "Détails de l'entreprise",
        "nav.contact": "Contact",
        "nav.faq": "FAQ",
        "nav.reviews": "Avis",
        "header.search": "Rechercher",
        "header.contact_support": "Contact & Support",
        "header.subtotal": "Sous-total",
        "header.search_products": "Rechercher des produits",
        "header.user_account": "Compte utilisateur",
        "header.close_menu": "Fermer le menu",
        "header.open_menu": "Ouvrir le menu",
        "featured.title": "Produits en vedette",
        "featured.subtitle": "Découvrez nos composants industriels les plus populaires",
        "products.view_details": "Voir les détails",
        "products.add_to_cart": "Ajouter au panier",
        "products.added": "Ajouté",
        "products.in_stock": "En stock",
        "products.out_of_stock": "Épuisé",
        "products.view_all": "Voir tous les produits",
        "filters.search_placeholder": "Rechercher par nom, type ou description...",
        "filters.active_filters": "Filtres actifs:",
        "filters.clear_all": "Tout effacer",
        "filters.showing_results": "Affichage des résultats",
        "filters.no_results": "Aucun produit trouvé",
        "banner.shipping": "Livraison standard gratuite en Allemagne dès 100€ d'achat →",
        "request_part.title": "Demander une pièce de rechange",
        "request_part.subtitle": "Vous ne trouvez pas la pièce dont vous avez besoin ? Soumettez une demande et notre équipe vous aidera à la localiser.",
        "request_part.success_title": "Demande soumise avec succès !",
        "request_part.success_desc": "Merci pour votre demande. Notre équipe vous répondra dans les plus brefs délais.",
        "request_part.prod_name": "Nom du produit *",
        "request_part.prod_name_placeholder": "Entrez le nom du produit ou le numéro de modèle",
        "request_part.manufacturer": "Fabricant *",
        "request_part.manufacturer_placeholder": "Entrez le nom du fabricant",
        "request_part.model": "Numéro de modèle/série",
        "request_part.model_placeholder": "Entrez le numéro de modèle ou de série",
        "request_part.desc": "Description *",
        "request_part.desc_placeholder": "Décrivez la pièce de rechange que vous recherchez",
        "request_part.email": "Votre adresse e-mail *",
        "request_part.email_placeholder": "votre@email.fr",
        "request_part.phone": "Numéro de téléphone",
        "request_part.phone_placeholder": "+33 1 XX XX XX XX",
        "request_part.submit": "Soumettre la demande",
        "request_part.submitting": "Envoi de la demande..."
    },
    es: {
        "nav.manufacturers": "Fabricantes",
        "nav.products": "Productos",
        "nav.request_part": "Solicitar pieza",
        "nav.industrial_purchase": "Compra industrial",
        "nav.company_details": "Detalles de la empresa",
        "nav.contact": "Contacto",
        "nav.faq": "FAQ",
        "nav.reviews": "Reseñas",
        "header.search": "Buscar",
        "header.contact_support": "Contacto y soporte",
        "header.subtotal": "Subtotal",
        "header.search_products": "Buscar productos",
        "header.user_account": "Cuenta de usuario",
        "header.close_menu": "Cerrar menú",
        "header.open_menu": "Abrir menú",
        "featured.title": "Productos destacados",
        "featured.subtitle": "Explore nuestros componentes industriales más populares",
        "products.view_details": "Ver detalles",
        "products.add_to_cart": "Añadir al carrito",
        "products.added": "Añadido",
        "products.in_stock": "En stock",
        "products.out_of_stock": "Agotado",
        "products.view_all": "Ver todos los productos",
        "filters.search_placeholder": "Buscar por nombre, tipo o descripción...",
        "filters.active_filters": "Filtros activos:",
        "filters.clear_all": "Borrar todo",
        "filters.showing_results": "Mostrando resultados",
        "filters.no_results": "No se encontraron productos",
        "banner.shipping": "Envío estándar gratuito en Alemania a partir de 100€ →",
        "request_part.title": "Solicitar una pieza de repuesto",
        "request_part.subtitle": "¿No encuentra la pieza de repuesto que necesita? Envíe una solicitud y nuestro equipo le ayudará a encontrarla.",
        "request_part.success_title": "¡Solicitud enviada con éxito!",
        "request_part.success_desc": "Gracias por su solicitud. Nuestro equipo se pondrá en contacto con usted en breve.",
        "request_part.prod_name": "Nombre del producto *",
        "request_part.prod_name_placeholder": "Introduzca el nombre del producto o el número de modelo",
        "request_part.manufacturer": "Fabricante *",
        "request_part.manufacturer_placeholder": "Introduzca el nombre del fabricante",
        "request_part.model": "Número de modelo/serie",
        "request_part.model_placeholder": "Introduzca el número de modelo o serie",
        "request_part.desc": "Descripción *",
        "request_part.desc_placeholder": "Describa la pieza de repuesto que busca",
        "request_part.email": "Su correo electrónico *",
        "request_part.email_placeholder": "su@email.es",
        "request_part.phone": "Número de teléfono",
        "request_part.phone_placeholder": "+34 91X XXX XXX",
        "request_part.submit": "Enviar solicitud",
        "request_part.submitting": "Enviando solicitud..."
    },
    hi: {
        "nav.manufacturers": "निर्माता",
        "nav.products": "उत्पाद",
        "nav.request_part": "स्पेयर पार्ट का अनुरोध करें",
        "nav.industrial_purchase": "औद्योगिक खरीद",
        "nav.company_details": "कंपनी विवरण",
        "nav.contact": "संपर्क करें",
        "nav.faq": "सामान्य प्रश्न",
        "nav.reviews": "समीक्षाएँ",

        "nav.payment": "भुगतान",
        "nav.returns": "रिटर्न",
        "nav.shipment": "शिपमेंट",
        "nav.conditions": "शर्तें",
        "header.search": "खोजें",
        "header.contact_support": "संपर्क और सहायता",
        "header.subtotal": "उपयोग",
        "header.search_products": "उत्पाद खोजें",
        "header.user_account": "उपयोगकर्ता खाता",
        "header.close_menu": "मेनू बंद करें",
        "header.open_menu": "मेनू खोलें",
        "featured.title": "विशेष उत्पाद",
        "featured.subtitle": "हमारे सबसे लोकप्रिय औद्योगिक घटकों का अन्वेषण करें",
        "products.view_details": "विवरण देखें",
        "products.add_to_cart": "कार्ट में जोड़ें",
        "products.added": "जोड़ा गया",
        "products.in_stock": "स्टॉक में",
        "products.out_of_stock": "स्टॉक खत्म",
        "products.view_all": "सभी उत्पाद देखें",
        "filters.search_placeholder": "नाम, प्रकार या विवरण द्वारा उत्पाद खोजें...",
        "filters.active_filters": "सक्रिय फिल्टर:",
        "filters.clear_all": "सभी साफ़ करें",
        "filters.showing_results": "परिणाम दिखा रहा है",
        "filters.no_results": "कोई उत्पाद नहीं मिला",
        "banner.shipping": "भारत में ₹10,000 से अधिक की खरीद पर मुफ्त मानक शिपings →",
        "wishlist.title": "मेरी इच्छा सूची",
        "wishlist.items": "वस्तुएं",
        "wishlist.empty_title": "आपकी इच्छा सूची खाली है",
        "wishlist.empty_subtitle": "बाद में सहेजने के लिए अपनी इच्छा सूची में आइटम जोड़ना शुरू करें",
        "wishlist.browse": "उत्पाद ब्राउज़ करें",
        "contact.title": "संपर्क करें",
        "contact.subtitle": "हमारे उत्पादों या सेवाओं के बारे में प्रश्न हैं? हम यहाँ मदद के लिए हैं।",
        "contact.send_message": "हमें एक संदेश भेजें",
        "contact.success_title": "संदेश भेज दिया गया!",
        "contact.success_msg": "हमसे संपर्क करने के लिए धन्यवाद। हम जल्द ही आपसे संपर्क करेंगे।",
        "contact.chat_whatsapp": "व्हाट्सएप पर चैट करें",
        "contact.send_another": "दूसरा संदेश भेजें",
        "hero.title": "औद्योगिक स्पेयर पार्ट्स और घटक",
        "hero.subtitle": "उच्च गुणवत्ता वाले औद्योगिक स्पेयर पार्ट्स, बियरिंग्स, पंप, मोटर और घटकों के लिए आपका विश्वसनीय साथी। 20 से अधिक वर्षों की उद्योग विशेषज्ञता के साथ, हम विश्वसनीयता और उत्कृष्टता प्रदान करते हैं।",
        "hero.browse": "उत्पाद ब्राउज़ करें",
        "account.my_orders": "मेरे आदेश",
        "account.view_all": "सभी देखें",
        "account.no_orders": "अभी तक कोई आदेश नहीं",
        "account.no_orders_msg": "जब आप कोई आदेश देंगे, तो वह यहाँ दिखाई देगा।",
        "account.start_shopping": "खरीदारी शुरू करें",
        "account.wishlist": "इच्छा-सूची",
        "account.wishlist_empty": "आपकी इच्छा-सूची खाली है",
        "account.my_cart": "मेरा कार्ट",
        "account.cart_empty": "आपका कार्ट खाली है",
        "account.phone": "फ़ोन",
        "account.address": "पता",
        "account.member_status": "सदस्यता स्थिति",
        "account.active": "सक्रिय",
        "account.joined": "शामिल हुए",
        "account.edit_profile": "प्रोफ़ाइल संपादित करें",
        "account.update_info": "अपनी जानकारी अपडेट करें",
        "account.title": "मेरा खाता",
        "account.hello": "नमस्ते",
        "account.dashboard": "डैशबोर्ड",
        "account.sign_out": "साइन आउट",
        "account.recent_activity": "अपनी हाल की खरीदारी पर नज़र रखें",
        "account.manage_desc": "अपनी प्रोफ़ाइल, आदेश और प्राथमिकताएं प्रबंधित करें",
        "hero.contact": "संपर्क करें",
        "products.dynamic_price": "कीमतें गतिशील हैं, यह एक अनुमानित लागत है, अंतिम उद्धरण और चालान के लिए ऑर्डर देने के बाद हमसे संपर्क करें।",
        "products.only_available": "केवल {count} उपलब्ध - अभी ऑर्डर करें!",
        "products.condition": "स्थिति",
        "products.condition_used": "उपयोग किया गया",
        "products.trust_payment": "चालान पर भुगतान - किसी खाते की आवश्यकता नहीं",
        "products.trust_shipping": "मुफ्त शिपिंग",
        "products.trust_delivery": "अगले दिन डिलीवरी उपलब्ध",
        "products.info": "उत्पाद जानकारी",
        "products.quantity": "मात्रा",
        "products.sku": "SKU",
        "products.bulk_title": "थोक ऑर्डर जांच",
        "products.bulk_send": "जांच भेजें",
        "products.desc": "विवरण",
        "products.specs": "विशेष विवरण",
        "products.shipping": "शिपिंग",
        "footer.company": "कंपनी",
        "footer.about": "हमारे बारे में",
        "footer.all_products": "सभी उत्पाद",
        "footer.support": "सहायता",
        "footer.legal": "कानूनी",
        "footer.privacy": "गोपनीयता नीति",
        "footer.terms": "सेवा की शर्तें",
        "footer.rights": "सर्वाधिकार सुरक्षित।",
        "category.pumps": "पंप",
        "category.motors": "मोटर",
        "faq.ordering": "आदेश देना",
        "faq.shipping": "शिपिंग",
        "faq.returns": "रिटर्न",
        "faq.payment_methods_q": "आप कौन सी भुगतान विधियां स्वीकार करते हैं?",
        "faq.payment_methods_a": "हम बैंक हस्तांतरण (प्रोफ़ार्मा), पेपाल, iDeal, मास्टरकार्ड और वीज़ा स्वीकार करते हैं। व्यावसायिक ग्राहक और सार्वजनिक संस्थान खाते पर भी भुगतान कर सकते हैं।",
        "faq.bulk_order_q": "क्या मैं थोक में ऑर्डर दे सकता हूँ?",
        "faq.bulk_order_a": "हाँ! हम थोक आदेशों के लिए विशेष मूल्य प्रदान करते हैं। कृपया हमारी बिक्री टीम से sales@unispare.in पर संपर्क करें या +91 97188 89253 पर कॉल करें।",
        "faq.shipping_time_q": "शिपिंग में कितना समय लगता है?",
        "faq.shipping_time_a": "यूरोपीय संघ और अमेरिका के लिए मानक वितरण में 3-6 कार्य दिवस और अन्य स्थानों के लिए 6-10 कार्य दिवस लगते हैं। दोपहर 4 बजे से पहले के ऑर्डर उसी दिन यूपीएस/शेंकर के माध्यम से भेजे जाते हैं।",
        "faq.international_q": "क्या आप अंतरराष्ट्रीय स्तर पर शिप करते हैं?",
        "faq.international_a": "हाँ, हम दुनिया भर में शिप करते हैं। यूरोपीय संघ के बाहर, ऑर्डर DDU (Delivered Duty Unpaid) भेजे जाते हैं - ग्राहक स्थानीय शुल्क और करों के लिए जिम्मेदार होते हैं।",
        "faq.return_policy_q": "आपकी वापसी नीति क्या है?",
        "faq.return_policy_a": "हम सभी उत्पादों पर 14-दिन की वापसी का अधिकार प्रदान करते हैं (अमेरिका/कनाडा के लिए 30 दिन)। आइटम टैग के साथ मूल स्थिति में होने चाहिए।",
        "faq.return_process_q": "मैं कोई आइटम कैसे वापस करूँ?",
        "faq.return_process_a": "रिटर्न हमारे रिटर्न पोर्टल के माध्यम से जमा किया जाना चाहिए। कृपया ध्यान दें कि रिटर्न शिपिंग लागत और कोई भी सीमा शुल्क ग्राहक की जिम्मेदारी है।",
        "request_part.title": "स्पेयर पार्ट का अनुरोध करें",
        "request_part.subtitle": "क्या आपको वह स्पेयर पार्ट नहीं मिल रहा जिसकी आपको आवश्यकता है? एक अनुरोध सबमिट करें और हमारी टीम इसे खोजने में आपकी सहायता करेगी।",
        "request_part.success_title": "अनुरोध सफलतापूर्वक सबमिट किया गया!",
        "request_part.success_desc": "आपके अनुरोध के लिए धन्यवाद। हमारी टीम जल्द ही आपसे संपर्क करेगी।",
        "request_part.prod_name": "उत्पाद का नाम *",
        "request_part.prod_name_placeholder": "उत्पाद का नाम या मॉडल संख्या दर्ज करें",
        "request_part.manufacturer": "निर्माता *",
        "request_part.manufacturer_placeholder": "निर्माता का नाम दर्ज करें",
        "request_part.model": "मॉडल/सीरियल संख्या",
        "request_part.model_placeholder": "मॉडल या सीरियल संख्या दर्ज करें",
        "request_part.desc": "विवरण *",
        "request_part.desc_placeholder": "उस स्पेयर पार्ट का विवरण दें जिसे आप ढूंढ रहे हैं",
        "request_part.email": "आपका ईमेल *",
        "request_part.email_placeholder": "your@email.com",
        "request_part.phone": "फ़ोन नंबर",
        "request_part.phone_placeholder": "+91 XXXXX XXXXX",
        "request_part.submit": "अनुरोध सबमिट करें",
        "request_part.submitting": "अनुरोध सबमिट किया जा रहा है..."
    }
}

export function LanguageProvider({ children }: { children: React.ReactNode }) {
    const [language, setLanguage] = useState<Language>("en")

    useEffect(() => {
        const saved = localStorage.getItem("preferred_language")
        if (saved && (saved in TRANSLATIONS)) {
            setLanguage(saved as Language)
        }
    }, [])

    const updateLanguage = (l: Language) => {
        setLanguage(l)
        localStorage.setItem("preferred_language", l)
    }

    const t = (key: string) => {
        return TRANSLATIONS[language][key] || key
    }

    return (
        <LanguageContext.Provider value={{ language, setLanguage: updateLanguage, t }}>
            {children}
        </LanguageContext.Provider>
    )
}

export function useLanguage() {
    const context = useContext(LanguageContext)
    if (context === undefined) {
        throw new Error("useLanguage must be used within a LanguageProvider")
    }
    return context
}
